/*
custom mixins you can import into Vue components in mixins: options

import { splTableMixin } from '../js/custom-vue-mixins';

mixins: [splTableMixin],

*/

import { errorMessageMaker } from './filters-helpers';
import get from 'lodash/get';
import { DateTime } from 'luxon';

var errorHandling = {
    data: function() {
        return {
            errorHandlingMessage: '',
            errorMsgList: [], // NEW - push new messages here, then have dismissable alerts to show each one
        }
    },
    methods: {
        resetErrors: function() {
            // clear all error messages -- call this from components where used, when you're repeating an API call or something
            this.errorHandlingMessage = '';
            this.errorMsgList = [];
        },
        apiErrorHandler: function(error, extraMsg, localMsg) {
            // localMsg is a BOOLEAN -- if TRUE, then do not show toast, but instead put message in this.errorHandlingMessage
            //       if you use localMsg = true, must make sure you will display a local alert / box for this.errorHandlingMessage (ie: use dismissable Bootstrap alert)
            // NEW Feb 4 2022 - explicitly sending error to DataDog 
            window.DD_RUM && window.DD_RUM.addError(error);  // addError also takes optional 2nd arg, a context object, can have any key/values

            let toastMessageArray = [];  // add to this as we go -- make an array of strings, we'll put each string on a new line
            let showMessage = true;  // should we show user message/toast at all?  default true, but can make it false as we go, then check at the end before actually showing toast
            if (extraMsg) {
                /* this.$toast.error(extraMsg, {
                    timeout: false,
                }); */  // don't show a separate toast... instead, just add this as first line in single toast
                toastMessageArray.push(extraMsg);
            }
            if (error.response) {
                // The request was made and the server responded with a status code that falls out of the range of 2xx
                const statusCode = error.response.status;  // will be an integer
                const is5xxError = !!statusCode && (statusCode < 600) && (statusCode >= 500);
                const is502Error = is5xxError && statusCode === 502;  // we will do special stuff with these
                console.error(`Got error response with status (${statusCode}) - response below:`);
                console.error(error.response);  // dump to console so front-end error logging picks it up
                // error.response is where all the good stuff is: { config, data, headers, request, status, statusText }
                // CHECK IF YOU GOT HTML RESPONSE BACK - if view is not DRF, you'll have text/html content type
                // check with error.response.headers['content-type'] - might be more than just 'text/html' - so check if it contains that - if so, then show a more generic message
                const responseContentType = error.response.headers['content-type'];
                // console.log('error.response content-type is', responseContentType);
                const responseIsHtml = responseContentType.includes('html');  // check for 'text' instead?
                // do not show error when tu mismatch happened

                if(statusCode === 400 && error.response.headers[window?.splData.TU_MISMATCH_HEADER_NAME] === 'true') {
                  showMessage = false;
                }

                if (responseIsHtml || is5xxError) {
                    if (is502Error) {
                        // special message for 502 Bad Gateway - no toast
                        showMessage = false;
                        console.error('Got 502 error - logging and not showing toast');
                        if (error.request) {
                            console.error('Request that failed:');
                            console.error(error.request);
                        }
                    } else {
                        // will you ever have JSON 500's?  I think you could... 
                        const defaultMsg = 'Something went wrong. Please contact support@supplios.com or click on the in-app Help widget below.';
                        toastMessageArray.push(defaultMsg);
                    }
                } else {
                    // data will be something like {detail: 'Some message you wrote in a django exception'}
                    // status will be the status code number like 500, 403, etc.
                    // satusText will be the description of that status code like 'Forbidden', or 'Bad Request', etc.
                    let errorMsg = errorMessageMaker(error.response.data);
                    toastMessageArray.push(errorMsg);
                }
            } else if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser
                // but based on seeing this happen, seems we always have empty object here for error.request
                // and this seems to be what happens if user navigates away from something that was in middle of request
                // so we'll still log error, but NOT show toast?  
                console.error(`Sent request, but no response received -- request below:`);
                console.error(error.request);
                // KEEP LOGGING THESE FOR NOW (console.error will get picked up by DataDog) - will help us find other front-end bugs
                // but we will NOT show toast messages to user... mostly false positives
                showMessage = false;
            } else {
                // Something happened in setting up the request that triggered an Error
                console.error(`Error while setting up request - message below:`);
                console.error('Error', error.message);
                // maybe also set showMessage = false here???
            }
            // console.error('Full error as JSON, then currentUserInfo:');
            try {
                console.error(error.toJSON());  // error.toJSON() is from axios... actually not a lot of useful stuff in here... but dump it anyways
            } catch {
                console.error(error);  // error.toJSON() only works for axios errors I believe
            }
            // try to grab currentUserInfo from window.currentUserInfo and send it to console.error
            // probably don't need this since datadog has all user data anyways - skipping it
            //console.error(window.currentUserInfo);  // WORKS
            //console.error('Error config:'); 
            //console.error(error.config);  // redundant?

            if (!showMessage) {
                return;  // just bail now if we don't want to show anything to user
            }

            // Now make the final string message to show, and show the toast -- or set this.errorHandlingMessage if localMsg = true
            let toastMessageString = (!!toastMessageArray && Array.isArray(toastMessageArray) && toastMessageArray.length) ? toastMessageArray.join('\n') : 'Something went wrong. Please contact Supplios Support.';
            if (localMsg) {
                // for now, use both ways... set message string, and push to array -- but use array from now on
                this.errorHandlingMessage = toastMessageString;
                this.errorMsgList.push(toastMessageString);
            } else {
                this.$toast.error(toastMessageString, {
                    timeout: false,
                });
            }
        },
        errorToaster: function(error, content) {
            // custom error handler that shows a dismissable message (toast) when error happens
            // also dumps things to the console
            let localContent = content || 'There was an error';
            let localError = error || 'Error';
            console.error(localContent);
            console.error(localError);  // should this be console.error???
            let errorMsg = errorMessageMaker(localContent);
            this.$toast.error(errorMsg, {
                timeout: false,
            });  
        },
        errorMessageMaker: function(content) {
            return errorMessageMaker(content);
        },
    },
};

const splTableDefaults = {
    // using this instead of splTableMixin for just holding defaults
    // then you can use it in prop validation -- seems you can't use component instance stuff in prop validation
    perPage: 10,
    perPageOptions: [5, 10, 25, 50],
};

/*  No longer used -- use non Vue mixin above -- more flexible
var splTableMixin = {
    data: function() {
        return {
            defaultTableSettings: {
                perPage: 10,
                perPageOptions: [5, 10, 25, 50],
            }
        }
    },
}; */

// you're using this to get stuff from Django base template and inject into each Vue instance
var currentUserInfoMixin = {
    computed: {
        currentUserInfo() {
            return this.$currentUserInfo;
        },
        $currentUserInfo() {  // adding $ for reducing chance of conflict
            let currentUserInfoObj = JSON.parse(document.getElementById('user-info').textContent);
            // adding in some 'derivative' things, just to avoid needing to do these things in each component
            // we are keeping with snake_case here on purpose
            //      since that's how the backend does it, and it matches the same property names on objects in backend
            if (!currentUserInfoObj) {
                return null;  
            }
            const is_external = currentUserInfoObj.is_internal === true ? false : undefined;  // being very explicit about it, although likely just !is_internal is adequate...
            // TODO - why the F did you (MH) return undefined for is_external?  Should be true or false...  must have been worried about some legacy thing... noticed Jan 5 2025 - fix it
            const info = { is_external, ...currentUserInfoObj };
            return info;
        },
        // you could add more things here, pull them out of $currentUserInfo for easier access...
    }
};

var tenantInfoMixin = {
    computed: {
        tenantInfo: function() {
            return this.$tenantInfo
        },
        $tenantInfo() {
            // gets tenantInfo JSON object from base Django template
            // top level things: { custom_flags, settings, tenant_name, tenant_id }
            let tenantInfoObj = JSON.parse(document.getElementById('tenant-info').textContent);
            return tenantInfoObj ? tenantInfoObj : null;
        },
    },
    methods: {
        $getTenantFlag(flag, defaultValue) {
            // return the value for a tenant custom_flags key --- can be 'nested.path', uses lodash get()
            // returns defaultValue (undefined if not specified) if does not have it 
            const flags = this.$tenantInfo.custom_flags || {};
            return get(flags, flag, defaultValue);
        },
        toTenantTz(value) {
            // convert value to the tenant TZ - and return the SAME TYPE!!!
            // pass in ISO8601?  convert then return ISO string
            // pass in Luxon?  convert and return Luxon
            if (!value) return value;  // return whatever falsy value it was
            const tenantTz = this.$tenantInfo.settings.timezone;  // tz name like 'America/New_York'
            if (typeof value === 'string') {
                const luxDT = DateTime.fromISO(value);
                return luxDT.isValid ? luxDT.setZone(tenantTz).toISO() : value;
            } else if (value instanceof Date) {
                const luxDT = DateTime.fromJSDate(value);
                return luxDT.isValid ? luxDT.setZone(tenantTz).toJSDate() : value;
            } else if (DateTime.isDateTime(value)) {
                return value.isValid ? value.setZone(tenantTz) : value;
            }
            return value;  // just return value if you don't know what it is
        },
        nowTenantTz() {
            const nowUTC = DateTime.utc();
            return this.toTenantTz(nowUTC);
        },
    }
};

// use this to dump key fixed URLs from python into JS via JSON script 
var splUrlMapMixin = {
    computed: {
        splUrlMap: function() {
            // gets id="spl-url-map" JSON object from based Django template
            return JSON.parse(document.getElementById('spl-url-map').textContent) || {};  // return empty object if not there
        },  // remove old version without $
        $splUrlMap: function() {
            // gets id="spl-url-map" JSON object from based Django template
            return JSON.parse(document.getElementById('spl-url-map').textContent) || {};  // return empty object if not there
        },
    }
};

var splUtilsMixin = {  // add this as global mixin in Vue!
    // DO NOT INCLUDE ANYTHING SENSITIVE - all Vue components (even 3rd party) will get this injected
    computed: {
        $splData: function() {
            // try getting spl_data that you added in context processor - an OBJECT (or empty object if not found in window.splData)
            // has these things:  'is_debug', 'SUPPLIOS_ENVIRONMENT', 'DD_ENV', 'DD_SERVICE', 'DD_VERSION', 'DD_TRACE_ENABLED' maybe more
            return window.splData ?? {};
        },
        $splEnv: function() {  // always returns a String  (empty string if not found)
            // return the current value for SUPPLIOS_ENVIRONMENT from django settings (via template context)
            const currentEnv = this.$splData.SUPPLIOS_ENVIRONMENT;
            return currentEnv ? currentEnv : '';
        },
        $splEnvIsDev: function() {
            // return Boolean if current environment is a dev environment
            // based on SUPPLIOS_ENVIRONMENT in django settings
            const devEnvList = ['notset', 'dev', 'local-dev', 'agent-dev'];
            return !!this.$splEnv && devEnvList.includes(this.$splEnv);
        },
    },
    methods: {
        $devlog: function(...thingsToLog) {
            // custom MH method for doing console.log only when DEBUG = true in Django template
            // can pass in multiple args, each will get console.log() to separate line
            // must have spl_data in template context
            if (this.$splData.is_debug) {
                thingsToLog.forEach(thing => console.log(thing));
            }
        },
    },
}


export {
    errorHandling,
    splTableDefaults,
    //splTableMixin,
    currentUserInfoMixin,
    tenantInfoMixin,
    splUrlMapMixin,
    splUtilsMixin,
}
